import { ReactNode } from 'react';
import { RaffleStatus, RaffleSubmissionData } from '@/generated/graphql';
import { RaffleTicketForm } from './RaffleTicketForm';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/Loader';
import { RaffleTicket } from '../RaffleTicket';
import { LogoutButton } from '@/components/LogoutButton';
import { motion } from 'motion/react';
import {
  disposalTypeForSubmissionId,
  raffleDisposalExit,
} from '../drawing/disposal';
import { FlameBurst } from '../drawing/FlameBurst';
import { FlyOffTrail } from '../drawing/FlyOffTrail';
import { GravityShadow } from '../drawing/GravityShadow';
import { WinnerConfetti } from '../drawing/WinnerConfetti';
import { RaffleStatusChip } from '../drawing/RaffleStatusChip';
import { ClaimPrizeBar } from '../drawing/ClaimPrizeBar';
import {
  raffleTicketFanVisibleSide,
  raffleTicketWiggleVariants,
  raffleWinnerTilt,
} from '../drawing/ticketMotion';
import { cn } from '@/lib/utils';
import {
  FAN_RAFFLE_COPY,
  type FanRafflePhase,
  raffleFanTicketMeta,
  shouldShowFanRaffleLogout,
  shouldShowFanRaffleTicket,
} from './fanChrome';
import { TypedSubmission } from '@/utils/engagementTypeUtils';

const CHIP_CLASS = 'px-3.5 py-1.5 text-[12px] tracking-[0.12em]';

const LOGOUT_CLASS =
  'h-12 w-full max-w-[300px] self-center rounded-full bg-[#f0f0f0] text-[16px] font-bold text-[#222] hover:bg-[#e8e8e8]';

export type FanRaffleScreenProps = {
  engagementId: string;
  status?: RaffleStatus | null;
  submission: { id: string; signatureImageUrl?: string | null } | null;
  submissionsLoading?: boolean;
  stillIn: boolean;
  phase: FanRafflePhase;
  isWinner: boolean;
  isLoser: boolean;
  serial: string | null;
  entryNumber: number | null;
  claimCopy: string;
  showEntryForm: boolean;
  banner?: string | null;
  skipTicketIntro?: boolean;
  freezeDisposal?: boolean;
  wiggling?: boolean;
  onTicketTap?: () => void;
  onSubmitted?: (submission: TypedSubmission<RaffleSubmissionData>) => void;
  isProduction?: boolean;
  logoutSlot?: ReactNode;
};

function FanRaffleHeader({
  chip,
  tone,
  chipClassName,
  title,
  titleClassName,
  sub,
  subClassName,
}: {
  chip?: string;
  tone?: 'muted' | 'orange' | 'green';
  chipClassName?: string;
  title?: string;
  titleClassName?: string;
  sub?: string;
  subClassName?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 text-center">
      {chip ? (
        <RaffleStatusChip tone={tone} className={cn(CHIP_CLASS, chipClassName)}>
          {chip}
        </RaffleStatusChip>
      ) : null}
      {title ? (
        <h1
          className={cn(
            'font-screen-body text-[28px] font-extrabold leading-tight text-white',
            titleClassName,
          )}
        >
          {title}
        </h1>
      ) : null}
      {sub ? (
        <p
          className={cn(
            'max-w-[300px] whitespace-pre-line font-screen-body text-[15px] leading-snug text-[#A0A0A0]',
            subClassName,
          )}
        >
          {sub}
        </p>
      ) : null}
    </div>
  );
}

export function FanRaffleScreen({
  engagementId,
  status,
  submission,
  submissionsLoading = false,
  stillIn,
  phase,
  isWinner,
  isLoser,
  serial,
  entryNumber,
  claimCopy,
  showEntryForm,
  banner,
  skipTicketIntro = false,
  freezeDisposal = false,
  wiggling = false,
  onTicketTap,
  onSubmitted,
  isProduction = import.meta.env.MODE === 'production',
  logoutSlot,
}: FanRaffleScreenProps) {
  const drawingStillIn =
    status === RaffleStatus.Drawing &&
    stillIn &&
    phase === 'idle' &&
    Boolean(submission);
  const drawingEliminated =
    status === RaffleStatus.Drawing && Boolean(submission) && !stillIn;
  const closedWithoutTicket = Boolean(
    status && status !== RaffleStatus.Open && !submission,
  );
  const disposalType = submission
    ? disposalTypeForSubmissionId(submission.id)
    : null;
  const showTicket = shouldShowFanRaffleTicket({
    hasSubmission: Boolean(submission),
    phase,
    status,
    stillIn,
  });
  const showLogout = shouldShowFanRaffleLogout({
    isProduction,
    status,
    hasSubmission: Boolean(submission),
    phase,
  });
  const ticketMeta = raffleFanTicketMeta({
    status,
    serial,
    entryNumber,
    drawingStillIn,
    drawingEliminated,
  });
  const ticketDimmed = isLoser || (drawingEliminated && phase === 'gone');
  const playDispose = phase === 'disposing' && !freezeDisposal;
  const ticketMotion = freezeDisposal
    ? 'freeze'
    : playDispose
      ? 'dispose'
      : 'rest';

  return (
    <div
      data-name="RAFFLE_FAN_SCREEN"
      className="flex h-full w-full flex-col items-center gap-5 px-6 pb-10 pt-8"
    >
      <div aria-hidden className="h-1 w-10 rounded-full bg-[#FF6B3D]" />

      {showEntryForm && (
        <FanRaffleHeader
          title={FAN_RAFFLE_COPY.entryTitle}
          sub={FAN_RAFFLE_COPY.entrySub}
          subClassName="text-[14px]"
        />
      )}

      {status === RaffleStatus.Open && submission && (
        <FanRaffleHeader
          chip={FAN_RAFFLE_COPY.submittedPill}
          tone="orange"
          title={FAN_RAFFLE_COPY.submittedTitle}
          titleClassName="text-[32px]"
          sub={FAN_RAFFLE_COPY.submittedSub}
          subClassName="text-[16px]"
        />
      )}

      {status === RaffleStatus.ReadyToDraw && (
        <FanRaffleHeader
          chip={FAN_RAFFLE_COPY.readyPill}
          tone="muted"
          title={FAN_RAFFLE_COPY.readyTitle}
          sub={FAN_RAFFLE_COPY.readySub}
        />
      )}

      {drawingStillIn && (
        <FanRaffleHeader
          chip={FAN_RAFFLE_COPY.stillInPill}
          tone="green"
          title={FAN_RAFFLE_COPY.stillInTitle}
          sub={FAN_RAFFLE_COPY.stillInSub}
        />
      )}

      {drawingEliminated && (
        <FanRaffleHeader
          chip={FAN_RAFFLE_COPY.eliminatedPill}
          tone="orange"
          title={FAN_RAFFLE_COPY.eliminatedTitle}
          sub={FAN_RAFFLE_COPY.eliminatedSub}
        />
      )}

      {isWinner && (
        <FanRaffleHeader
          chip={FAN_RAFFLE_COPY.winnerPill}
          tone="orange"
          title={FAN_RAFFLE_COPY.winnerTitle}
          sub={FAN_RAFFLE_COPY.winnerSub}
        />
      )}

      {isLoser && (
        <FanRaffleHeader
          chip={FAN_RAFFLE_COPY.loserPill}
          tone="muted"
          chipClassName="border-white/20 bg-white/[0.06] text-white/65"
          title={FAN_RAFFLE_COPY.loserTitle}
          sub={FAN_RAFFLE_COPY.loserSub}
        />
      )}

      {closedWithoutTicket && status === RaffleStatus.Drawing && (
        <p className="text-center font-screen-body text-[15px] text-[#A0A0A0]">
          {FAN_RAFFLE_COPY.readySub}
        </p>
      )}

      {banner ? (
        <p className="text-center font-screen-body text-xl">{banner}</p>
      ) : null}

      {submissionsLoading && <Loader />}

      {showEntryForm && onSubmitted && (
        <RaffleTicketForm
          engagementId={engagementId}
          onSubmitted={onSubmitted}
          skipIntro={skipTicketIntro}
          className="w-full"
        />
      )}

      {showEntryForm && !onSubmitted && <FanRaffleEntryStatic />}

      {submission && showTicket && (
        <div
          className={cn(
            'mx-auto flex w-full max-w-[340px] flex-col items-center gap-4 overflow-visible',
            drawingEliminated && 'mt-3',
          )}
        >
          <motion.div
            className={cn(
              'relative w-full',
              freezeDisposal ? 'overflow-hidden' : 'overflow-visible',
              isWinner && 'pt-6',
            )}
            initial="rest"
            animate={ticketMotion}
            variants={{
              rest: { opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 },
              dispose: raffleDisposalExit(submission.id),
              freeze: {
                opacity: 0.88,
                x: 0,
                y: 0,
                scale: 1,
                rotate: 0,
              },
            }}
          >
            {drawingStillIn && (
              <motion.div
                aria-hidden
                data-name="RAFFLE_FAN_STILL_IN_RING"
                className="pointer-events-none absolute -inset-3.5 rounded-2xl border-2 border-[#50DC78]/55"
                animate={{
                  boxShadow: [
                    '0 0 8px rgba(80,220,120,0.12)',
                    '0 0 28px rgba(80,220,120,0.45)',
                    '0 0 8px rgba(80,220,120,0.12)',
                  ],
                  opacity: [0.65, 1, 0.65],
                }}
                transition={{
                  duration: 1.2,
                  ease: 'easeInOut',
                  repeat: Infinity,
                }}
              />
            )}
            {playDispose && disposalType === 'fly-off' && (
              <FlyOffTrail submissionId={submission.id} />
            )}
            {playDispose && disposalType === 'gravity' && <GravityShadow />}
            <motion.button
              type="button"
              className="relative w-full cursor-pointer appearance-none border-0 bg-transparent p-0"
              data-name="RAFFLE_FAN_TICKET_TAP"
              initial="rest"
              animate={wiggling ? 'wiggle' : 'rest'}
              variants={raffleTicketWiggleVariants}
              onClick={() => {
                if (phase !== 'idle') {
                  return;
                }
                onTicketTap?.();
              }}
            >
              <RaffleTicket
                signatureImageUrl={submission.signatureImageUrl}
                visibleSide={
                  showEntryForm ? 'back' : raffleTicketFanVisibleSide()
                }
                isWinner={isWinner}
                selected={phase === 'selected'}
                dimmed={ticketDimmed}
                stillIn={drawingStillIn}
                showWinnerBadge={isWinner}
                winnerTilt={isWinner ? raffleWinnerTilt(submission.id) : 0}
                className="w-full"
              />
              {(playDispose || freezeDisposal) && disposalType === 'flames' && (
                <FlameBurst staticVisible={freezeDisposal} />
              )}
            </motion.button>
            <WinnerConfetti
              submissionId={submission.id}
              active={isWinner && !freezeDisposal}
            />
          </motion.div>

          {drawingStillIn && (
            <p className="font-screen-body text-[22px] font-bold uppercase tracking-[0.08em] text-[#50DC78] [text-shadow:0_0_20px_rgba(80,220,120,0.45)]">
              {FAN_RAFFLE_COPY.stillInLabel}
            </p>
          )}

          {ticketMeta ? <TicketMetaLine meta={ticketMeta} /> : null}

          {isWinner && <ClaimPrizeBar copy={claimCopy} variant="card" />}

          {showLogout &&
            (logoutSlot ?? (
              <LogoutButton
                goTo={window.location.href}
                className={LOGOUT_CLASS}
              />
            ))}
        </div>
      )}
    </div>
  );
}

function TicketMetaLine({ meta }: { meta: string }) {
  return (
    <p className="text-center font-screen-body text-[13px] text-white/50">
      {meta.split(' · ').map((part, index) => (
        <span key={`${part}-${index}`}>
          {index > 0 ? ' · ' : null}
          <MetaPart part={part} />
        </span>
      ))}
    </p>
  );
}

function MetaPart({ part }: { part: string }) {
  const ticketMatch = part.match(/^Ticket (\d+)$/);
  if (ticketMatch) {
    return (
      <>
        Ticket{' '}
        <strong className="font-semibold text-white">{ticketMatch[1]}</strong>
      </>
    );
  }
  if (part.startsWith('Entry #')) {
    return (
      <>
        Entry{' '}
        <strong className="font-semibold text-white">{part.slice(6)}</strong>
      </>
    );
  }
  if (/^#\d+$/.test(part) || /^\d{6}$/.test(part)) {
    return <strong className="font-semibold text-white">{part}</strong>;
  }
  return part;
}

function FanRaffleEntryStatic() {
  return (
    <div className="mx-auto flex w-full max-w-[340px] flex-col items-center gap-7">
      <div className="relative w-full" style={{ aspectRatio: '2' }}>
        <RaffleTicket visibleSide="back" className="relative w-full" />
        <div
          aria-hidden
          data-name="RAFFLE_SIGN_HERE"
          className="border-black/32 pointer-events-none absolute inset-[21%_14%] z-[5] flex items-end justify-center rounded-lg border-[1.5px] border-dashed pb-2.5"
        >
          <span className="text-black/32 font-screen-body text-[13px] italic">
            {FAN_RAFFLE_COPY.signHere}
          </span>
        </div>
      </div>
      <div className="flex w-full justify-center gap-3">
        <Button
          size="xl"
          className="h-12 flex-1 rounded-xl bg-white text-[16px] font-bold text-[#111] hover:bg-white/90"
        >
          Clear
        </Button>
        <Button
          size="xl"
          className="h-12 flex-1 rounded-xl bg-[#FF6B3D] text-[16px] font-bold text-white shadow-[0_4px_18px_rgba(255,107,61,0.5)] hover:bg-[#FF6B3D]/90"
        >
          Submit
        </Button>
      </div>
    </div>
  );
}
